import { useCallback, useEffect, useRef, useState } from 'react';
import { LineChart, type ChartEvent } from '@/components/charts/LineChart';
import type { Point } from '@/components/charts/scale';
import {
  ActionKey,
  Badge,
  Button,
  Switch,
  TableSkeleton,
  StepProgress,
  type OperationStep,
} from '@/components/ui';
import { cn } from '@/components/ui/cn';
import { Demo } from './Demo';

/*
  Вторая партия приёмов «Характера» (блоки 6–12): график, который помнит события и показывает
  прогноз; остывание после инцидента; приезд новой строки; клавиши по Alt; заглушка, знающая
  форму данных; честный ход длинной операции. Всё живое, как в первой партии.
*/

// ---------- данные ----------

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** CPU за 12 часов: спокойно, затем ступенька сразу после выкладки. */
function cpuWithDeploy(now: number): { points: Point[]; events: ChartEvent[] } {
  const rnd = mulberry32(7);
  const step = 10 * 60 * 1000;
  const start = now - 12 * 3600 * 1000;
  const deployAt = start + 5 * 3600 * 1000;
  const migrationAt = start + 8.5 * 3600 * 1000;
  const rebootAt = start + 10.5 * 3600 * 1000;
  const points: Point[] = [];
  for (let t = start; t <= now; t += step) {
    const base = 22 + 4 * Math.sin((t - start) / (2.5 * 3600 * 1000));
    const afterDeploy = t >= deployAt ? 26 : 0;
    const afterReboot = t >= rebootAt ? -14 : 0;
    const v = base + afterDeploy + afterReboot + (rnd() - 0.5) * 3;
    points.push({ x: t, y: Math.round(Math.max(4, v) * 10) / 10 });
  }
  return {
    points,
    events: [
      { x: deployAt, kind: 'deploy', label: 'Выкладка 1.14.2, агент метрик' },
      { x: migrationAt, kind: 'migration', label: 'Переезд двух машин с host-341' },
      { x: rebootAt, kind: 'reboot', label: 'Перезагрузка после обновления ядра' },
    ],
  };
}

/** Диск за 30 дней плюс 12 дней прогноза. */
function diskWithForecast(now: number): { points: Point[]; forecast: Point[] } {
  const rnd = mulberry32(19);
  const day = 24 * 3600 * 1000;
  const points: Point[] = [];
  for (let i = 30; i >= 0; i--) {
    const v = 77 - i * 1.2 + (rnd() - 0.5) * 1.2;
    points.push({ x: now - i * day, y: Math.round(v * 10) / 10 });
  }
  const forecast: Point[] = [];
  for (let i = 1; i <= 12; i++) {
    forecast.push({ x: now + i * day, y: Math.round((77 + i * 1.25) * 10) / 10 });
  }
  return { points, forecast };
}

const nodes = [
  { id: 'host-338', role: 'Гипервизор · Площадка А', problem: true },
  { id: 'host-341', role: 'Гипервизор · Площадка А', problem: false },
  { id: 'host-011', role: 'Гипервизор · Площадка Б', problem: false },
];

const feedLines = [
  'Проверка диска завершена на host-341',
  'Машина 100101 переехала на host-354',
  'Порог загрузки снят на host-011',
  'Резервная копия базы завершена',
  'Обновление ядра применено на host-338',
  'Сеть восстановлена в стойке R-A',
  'Новая машина 100102 создана',
];

// ---------- 6. События на графике ----------

function ChartEventsDemo() {
  const [data] = useState(() => cpuWithDeploy(Date.now()));
  return (
    <div>
      <LineChart
        series={[{ id: 'cpu', label: 'Процессор', points: data.points }]}
        events={data.events}
        unit="%"
        yMax={80}
        height={220}
        ariaLabel="Загрузка процессора за 12 часов с отметками событий"
      />
      <p className="mt-3 text-xs text-fg-subtle">
        Наведите на ромб у верхнего края: система скажет, что это было и когда.
      </p>
    </div>
  );
}

// ---------- 7. Прогноз ----------

function ForecastDemo() {
  const [data] = useState(() => diskWithForecast(Date.now()));
  return (
    <div>
      <LineChart
        series={[{ id: 'disk', label: 'Диск', points: data.points }]}
        forecast={{ points: data.forecast, label: '90 % примерно через 11 дней' }}
        threshold={{ value: 90, label: 'порог 90 %' }}
        unit="%"
        yMax={100}
        height={220}
        ariaLabel="Заполнение диска за 30 дней и прогноз на 12 дней вперёд"
      />
    </div>
  );
}

// ---------- 8. Остывание ----------

function CoolingDemo() {
  const [coolingKey, setCoolingKey] = useState(0);
  const [cooling, setCooling] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-md border border-line">
        {nodes.map((n) => {
          const isCooling = cooling && n.problem;
          return (
            <div
              key={`${n.id}-${isCooling ? coolingKey : 'idle'}`}
              className={cn(
                'flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0',
                isCooling && 'animate-cool-down motion-reduce:animate-none',
              )}
            >
              <span className="w-20 shrink-0 text-xs font-medium text-fg">{n.id}</span>
              <span className="flex-1 truncate text-xs text-fg-subtle">{n.role}</span>
              {n.problem ? (
                cooling ? (
                  <span className="text-2xs text-fg-subtle">инцидент закрыт только что</span>
                ) : (
                  <Badge variant="urgent" solid>
                    Проблема
                  </Badge>
                )
              ) : (
                <Badge variant="success" solid>
                  Норма
                </Badge>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setCooling(true);
            setCoolingKey((k) => k + 1);
          }}
        >
          Закрыть инцидент на host-338
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setCooling(false)}>
          Вернуть как было
        </Button>
        <span className="text-xs text-fg-subtle">
          Строка не гаснет мгновенно: тёплый след держится и медленно уходит.
        </span>
      </div>
    </div>
  );
}

// ---------- 9. Приезд новой строки ----------

function ArrivalDemo() {
  const [live, setLive] = useState(true);
  const [items, setItems] = useState(() =>
    feedLines.slice(0, 5).map((t, i) => ({ id: `seed-${i}`, text: t, at: '—' })),
  );
  const boxRef = useRef<HTMLDivElement>(null);
  const counter = useRef(0);

  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => {
      counter.current += 1;
      const box = boxRef.current;
      const keepOffset = box && box.scrollTop > 2;
      setItems((prev) => [
        {
          id: `new-${counter.current}`,
          text: feedLines[counter.current % feedLines.length],
          at: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
        },
        ...prev.slice(0, 11),
      ]);
      // Если читают ниже начала списка — держим текст на месте, а не тащим его вниз.
      if (box && keepOffset) {
        window.requestAnimationFrame(() => {
          box.scrollTop += 33;
        });
      }
    }, 2600);
    return () => window.clearInterval(id);
  }, [live]);

  return (
    <div className="flex flex-col gap-4">
      <div ref={boxRef} className="h-40 overflow-y-auto rounded-md border border-line" aria-live="polite">
        {items.map((it, i) => (
          <div
            key={it.id}
            className={cn(
              'flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0',
              it.id.startsWith('new-') && i === 0 && 'animate-arrive motion-reduce:animate-none',
            )}
          >
            <span className="w-10 shrink-0 text-2xs text-fg-subtle tabular-nums">{it.at}</span>
            <span className="flex-1 truncate text-xs text-fg">{it.text}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Switch size="sm" checked={live} onCheckedChange={setLive} label="Поток событий" />
        <span className="text-xs text-fg-subtle">
          Прокрутите список вниз и подождите: строка приедет сверху, но текст под курсором не сдвинется.
        </span>
      </div>
    </div>
  );
}

// ---------- 10. Клавиши по Alt ----------

function KeyHintsDemo() {
  const actions = [
    { label: 'Подтвердить', combo: 'A' },
    { label: 'Заглушить', combo: 'S' },
    { label: 'Открыть', combo: '⏎' },
    { label: 'Фильтр', combo: '/' },
  ];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <Button key={a.label} variant="secondary" size="sm">
            {a.label}
            <ActionKey combo={a.combo} />
          </Button>
        ))}
      </div>
      <p className="text-xs text-fg-subtle">
        Нажмите и удерживайте Alt: у каждого действия проступит его клавиша. Отпустили — экран снова чистый.
      </p>
    </div>
  );
}

// ---------- 11. Заглушка, знающая форму ----------

const skeletonColumns = [
  { width: 'w-20' },
  { width: 'w-16' },
  { width: 'w-12', align: 'right' as const },
  { width: 'w-12', align: 'right' as const },
];

function SkeletonDemo() {
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    window.setTimeout(() => setLoading(false), 1600);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border border-line px-3">
        {loading ? (
          <TableSkeleton columns={skeletonColumns} rows={3} />
        ) : (
          <div>
            <div className="flex h-10 items-center gap-4 border-b border-line text-2xs text-fg-subtle">
              <span className="flex-1">Узел</span>
              <span className="flex-1">Состояние</span>
              <span className="flex-1 text-right">Процессор</span>
              <span className="flex-1 text-right">Память</span>
            </div>
            {[
              { id: 'host-338', state: 'Проблема', cpu: '34 %', mem: '61 %' },
              { id: 'host-341', state: 'Норма', cpu: '18 %', mem: '44 %' },
              { id: 'host-011', state: 'Норма', cpu: '71 %', mem: '81 %' },
            ].map((r) => (
              <div key={r.id} className="flex h-10 items-center gap-4 border-b border-line last:border-b-0">
                <span className="flex-1 text-xs font-medium text-fg">{r.id}</span>
                <span className="flex-1 text-xs text-fg-muted">{r.state}</span>
                <span className="flex-1 text-right text-xs text-fg tabular-nums">{r.cpu}</span>
                <span className="flex-1 text-right text-xs text-fg tabular-nums">{r.mem}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={reload} disabled={loading}>
          Загрузить заново
        </Button>
        <span className="text-xs text-fg-subtle">
          Высота, колонки и выравнивание те же, что у данных: при появлении ничего не прыгает.
        </span>
      </div>
    </div>
  );
}

// ---------- 12. Честный ход операции ----------

const migrationSteps: { label: string; note?: string }[] = [
  { label: 'Проверка свободного места на приёмнике' },
  { label: 'Снимок состояния машины' },
  { label: 'Перенос диска', note: '12 ГБ' },
  { label: 'Переключение сети' },
  { label: 'Запуск и проверка доступности' },
];

function OperationDemo() {
  const [progress, setProgress] = useState(-1);
  const [failAt, setFailAt] = useState<number | null>(null);
  const timer = useRef(0);

  const stop = useCallback(() => {
    window.clearTimeout(timer.current);
  }, []);

  useEffect(() => stop, [stop]);

  useEffect(() => {
    if (progress < 0 || progress >= migrationSteps.length) return;
    if (failAt !== null && progress === failAt) return;
    timer.current = window.setTimeout(() => setProgress((p) => p + 1), 1400);
    return () => window.clearTimeout(timer.current);
  }, [progress, failAt]);

  const steps: OperationStep[] = migrationSteps.map((s, i) => {
    let status: OperationStep['status'] = 'pending';
    if (failAt !== null && i === failAt && progress >= i) status = 'failed';
    else if (progress > i) status = 'done';
    else if (progress === i) status = 'active';
    return {
      id: s.label,
      label: s.label,
      status,
      note:
        status === 'failed' ? 'нет места на приёмнике' : status === 'active' ? (s.note ?? 'идёт') : undefined,
    };
  });

  const running = progress >= 0 && progress < migrationSteps.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border border-line p-3">
        {progress < 0 ? (
          <p className="text-xs text-fg-subtle">Операция не запущена.</p>
        ) : (
          <StepProgress steps={steps} />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={running}
          onClick={() => {
            stop();
            setFailAt(null);
            setProgress(0);
          }}
        >
          Запустить переезд
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={running}
          onClick={() => {
            stop();
            setFailAt(2);
            setProgress(0);
          }}
        >
          Сценарий со сбоем
        </Button>
        <span className="text-xs text-fg-subtle">
          Вместо крутилки видно, что сделано, что идёт и где встало.
        </span>
      </div>
    </div>
  );
}

// ---------- сборка ----------

export function PartTwo({ startIndex }: { startIndex: number }) {
  const demos = [
    {
      title: 'График помнит, что происходило',
      idea: 'Выкладки, переезды, перезагрузки и окна обслуживания стоят засечками прямо на линии. Наведение говорит, что это было.',
      gain: 'Отвечает на вопрос, который в разборе задают первым: после чего началось. Обычно на это уходит десять минут в чужом журнале изменений.',
      node: <ChartEventsDemo />,
    },
    {
      title: 'Линия продолжается в будущее',
      idea: 'Прогноз нарисован тем же пером, но пунктиром и полупрозрачно: видно, что это ещё не факт, а расчёт. Подпись говорит, когда упрёмся в порог.',
      gain: 'Место кончается предсказуемо, а узнают об этом обычно ночью по алерту. Здесь это видно за полторы недели.',
      node: <ForecastDemo />,
    },
    {
      title: 'Объект остывает, а не гаснет',
      idea: 'Вернулся в норму — тёплый след держится и медленно уходит. Строка какое-то время помнит, что здесь только что горело.',
      gain: 'Ничего не исчезает молча, пока ты отвернулся. Вернулся через минуту и всё ещё видишь, где было жарко.',
      node: <CoolingDemo />,
    },
    {
      title: 'Новое приезжает, а не прыгает',
      idea: 'Новая строка проявляется сверху с короткой подсветкой края. Если читаешь ниже начала списка, текст под курсором остаётся на месте.',
      gain: 'Разница между «читаю ленту» и «гоняюсь за строкой». В потоке алертов это экономит нервы и клики мимо.',
      node: <ArrivalDemo />,
    },
    {
      title: 'Клавиши проступают сами',
      idea: 'Пока Alt не нажат, экран чистый. Удержал — у каждого действия появилась его клавиша.',
      gain: 'Интерфейс учит себя вместо справки, и при этом не засоряет кнопки подписями на каждый день.',
      node: <KeyHintsDemo />,
    },
    {
      title: 'Заглушка знает форму данных',
      idea: 'Пока данные едут, на их месте стоит та же сетка: те же колонки, та же высота строки, то же выравнивание чисел.',
      gain: 'При появлении данных ничего не прыгает. Взгляд уже стоит там, где будет нужное число.',
      node: <SkeletonDemo />,
    },
    {
      title: 'Длинная операция идёт честно',
      idea: 'Вместо бесконечной крутилки — шаги: что сделано, что идёт сейчас, что впереди. Сбой показывает, на каком шаге и почему.',
      gain: 'Видно, что процесс движется, и сразу понятно, где встал. Не нужно лезть в журнал, чтобы это выяснить.',
      node: <OperationDemo />,
    },
  ];

  return (
    <>
      {demos.map((d, i) => (
        <Demo key={d.title} n={startIndex + i} title={d.title} idea={d.idea} gain={d.gain}>
          {d.node}
        </Demo>
      ))}
    </>
  );
}
